import {
  AdornedInput,
  Adornment,
  Card,
  DetailedIconHome,
  Flex,
  FormField,
  FormHelp,
  Heading,
  IconCalendarOutline,
  Input,
  Label,
  Paragraph,
  Select,
  Textarea,
} from '@zillow/constellation';
import { Controller, useFormContext } from 'react-hook-form';
import { get } from 'lodash-es';
import { FIELD_IDS } from '../../../../constants/wizard';
import { ResidenceType } from '../../../../graphql/queries';
import { MaskedCurrencyInput, MaskedPhoneInput, MaskedZipCodeInput, validateZipCode } from '../../../Inputs';
import { date50YearsAgo, isValidUSPhoneNumber, today } from '../../../../utils/formatters';
import { useEffect } from 'react';
import { TEXT_AREA_MAX_LENGTH_510 } from '../../../../constants/form';
import { US_STATES_ABBR_LIST } from '../../../../constants/applicationsConstants';
import { validateDate, validateEmail } from '../../../../utils/validation';
import { DatePickerInputMemoized } from '../../../DatePicker';
import { validateCurrency } from '../../../Inputs/MaskedCurrencyInput';
import { ComboBoxFix } from '../../../Combobox';

export function CurrentResidenceStep() {
  const {
    formState: { errors },
    getValues,
    register,
    setValue,
    trigger,
    watch,
  } = useFormContext();

  const homeType = watch('currentResidence.type');

  useEffect(() => {
    // Default type to rent if none is set initially
    if (!homeType) {
      setValue('currentResidence.type', ResidenceType.rent);
      trigger('currentResidence.type');
    }
  }, [homeType, setValue, trigger]);

  const errorStreet = get(errors, 'currentResidence.address.street');
  const errorCity = get(errors, 'currentResidence.address.city');
  const errorState = get(errors, 'currentResidence.address.state');
  const errorZip = get(errors, 'currentResidence.address.zip');
  const errorMonthlyPayment = get(errors, 'currentResidence.monthlyPayment');

  return (
    <>
      <Heading level={4} as="h2" marginBottom="sm" fontFamily="serif">
        Current residence
      </Heading>
      <Flex display="flex" flexDirection="column" gap="sm">
        <Controller
          name="currentResidence.type"
          render={({ field: { onChange, value, onBlur } }) => (
            <FormField
              label={<Label>Type</Label>}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                let fieldsToClear: Array<string> = [];
                if (e.target.value !== ResidenceType.rent) {
                  fieldsToClear = [
                    ...fieldsToClear,
                    'currentResidence.manager.name',
                    'currentResidence.manager.email',
                    'currentResidence.manager.phone',
                  ];
                }
                if (e.target.value !== ResidenceType.other) {
                  fieldsToClear = [...fieldsToClear, 'currentResidence.description'];
                }
                if (e.target.value === ResidenceType.other) {
                  fieldsToClear = [...fieldsToClear, FIELD_IDS.REASON_FOR_MOVING];
                }
                fieldsToClear.forEach((fieldToClear) => setValue(fieldToClear, null));
                onChange(e.target.value);
              }}
              control={
                <Select value={value || ''} onBlur={onBlur}>
                  <option value={ResidenceType.rent}>A home I rent</option>
                  <option value={ResidenceType.own}>A home I own</option>
                  <option value={ResidenceType.other}>A different type of home</option>
                </Select>
              }
            />
          )}
        />
        <FormField
          label={<Label>Street</Label>}
          control={
            <Input
              maxLength={60}
              {...register('currentResidence.address.street', {
                required: 'Street is required',
              })}
            />
          }
          description={errorStreet && <FormHelp>{errorStreet.message}</FormHelp>}
          error={!!errorStreet}
        />
        <FormField
          label={<Label>City</Label>}
          control={
            <Input
              maxLength={32}
              {...register('currentResidence.address.city', {
                required: 'City is required',
              })}
            />
          }
          description={errorCity && <FormHelp>{errorCity.message}</FormHelp>}
          error={!!errorCity}
        />
        <FormField
          label={<Label>State</Label>}
          control={
            <ComboBoxFix
              {...register('currentResidence.address.state', {
                required: 'State is required',
              })}
              placeholder="Select a state"
              options={US_STATES_ABBR_LIST}
              size="md"
              defaultValue={getValues('currentResidence.address.state')}
              onChange={(selectedValue: string) => {
                setValue('currentResidence.address.state', selectedValue);
                trigger('currentResidence.address.state');
              }}
            />
          }
          description={errorState && <FormHelp>{errorState.message}</FormHelp>}
          error={!!errorState}
        />
        <FormField
          label={<Label>ZIP code</Label>}
          control={
            <Controller
              name="currentResidence.address.zip"
              rules={{
                required: 'ZIP code is required',
                validate: validateZipCode,
              }}
              render={({ field }) => <MaskedZipCodeInput {...field} />}
            />
          }
          description={errorZip && <FormHelp>{errorZip.message}</FormHelp>}
          error={!!errorZip}
        />
        <Controller
          name="currentResidence.startDate"
          rules={{
            required: 'A moved in date is required',
            validate: (v) =>
              validateDate(v, {
                inThePast: true,
                maxYearsAgo: 50,
              }),
          }}
          render={({ field, fieldState: { error, invalid } }) => (
            <FormField
              label={<Label>Moved in</Label>}
              description={error && <FormHelp>{error?.message}</FormHelp>}
              error={invalid}
            >
              <AdornedInput
                input={<DatePickerInputMemoized {...field} minDate={date50YearsAgo()} maxDate={today()} />}
                rightAdornment={
                  <Adornment>
                    <IconCalendarOutline size="sm" />
                  </Adornment>
                }
              />
            </FormField>
          )}
        />
        <Controller
          name={FIELD_IDS.CURRENT_RESIDENCE_AMOUNT}
          rules={{ required: 'Monthly amount is required', validate: validateCurrency }}
          render={({ field }) => (
            <FormField
              label={<Label>Monthly {homeType === ResidenceType.own ? 'mortgage' : 'rent'}</Label>}
              control={<MaskedCurrencyInput {...field} />}
              description={errorMonthlyPayment && <FormHelp>{errorMonthlyPayment.message}</FormHelp>}
              error={!!errorMonthlyPayment}
            />
          )}
        />
        {(homeType === ResidenceType.rent || homeType === ResidenceType.own) && (
          <FormField
            label={<Label>Reason for moving</Label>}
            control={
              <Textarea
                maxLength={TEXT_AREA_MAX_LENGTH_510}
                {...register(FIELD_IDS.REASON_FOR_MOVING, {
                  required: 'A reason for moving is required',
                })}
                resize="vertical"
              />
            }
            description={errors.reasonForMoving && <FormHelp>{errors.reasonForMoving.message}</FormHelp>}
            error={!!errors.reasonForMoving}
          />
        )}
        {homeType === ResidenceType.rent && <LandlordContact />}
        {homeType === ResidenceType.other && (
          <FormField
            label={<Label>Description</Label>}
            control={<Textarea {...register('currentResidence.description')} resize="vertical" />}
            optional
          />
        )}
      </Flex>
    </>
  );
}

export function CurrentResidenceStepTip() {
  return (
    <Card cardElevation={0} cardType="secondary">
      <DetailedIconHome marginBottom="sm" />
      <Heading level={4} as="h2" marginBottom="sm" fontFamily="serif">
        Your residence history
      </Heading>

      <Paragraph>
        Rental managers like to know where you’ve lived and have an opportunity to check your references.
      </Paragraph>
      <Paragraph>Rental managers may use this to perform reference checks.</Paragraph>
    </Card>
  );
}

function LandlordContact() {
  const {
    formState: { errors },
    register,
  } = useFormContext();

  const errorLandlordName = get(errors, 'currentResidence.manager.name');
  const errorLandlordEmail = get(errors, 'currentResidence.manager.email');
  const errorLandlordPhone = get(errors, 'currentResidence.manager.phone');

  return (
    <>
      <FormField
        label={<Label>Landlord name</Label>}
        control={
          <Input
            maxLength={100}
            {...register('currentResidence.manager.name', {
              required: 'A name is required',
            })}
          />
        }
        description={errorLandlordName && <FormHelp>{errorLandlordName.message}</FormHelp>}
        error={!!errorLandlordName}
      />
      <FormField
        label={<Label>Landlord contact email</Label>}
        control={
          <Input
            maxLength={100}
            {...register('currentResidence.manager.email', {
              validate: validateEmail,
            })}
            type="email"
          />
        }
        description={errorLandlordEmail && <FormHelp>{errorLandlordEmail.message}</FormHelp>}
        error={!!errorLandlordEmail}
        optional
      />
      <FormField
        label={<Label>Landlord contact phone</Label>}
        control={
          <Controller
            name="currentResidence.manager.phone"
            rules={{
              required: 'A phone number is required',
              validate: (phone) => isValidUSPhoneNumber(phone) || 'Enter a valid phone number',
            }}
            render={({ field }) => <MaskedPhoneInput {...field} />}
          />
        }
        description={errorLandlordPhone && <FormHelp>{errorLandlordPhone.message}</FormHelp>}
        error={!!errorLandlordPhone}
      />
    </>
  );
}
